import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T } from "@/components/editable/T";
import { EditableImage } from "@/components/editable/EditableImage";
import quoteLogo from "@/assets/logos/press-quote.webp";
import rabobankLogo from "@/assets/logos/press-rabobank.webp";
import deloitteLogo from "@/assets/logos/press-deloitte.webp";
import werfLogo from "@/assets/logos/press-werf-en.webp.asset.json";
import ondernemerLogo from "@/assets/logos/press-deondernemer.webp.asset.json";
import arbeidsvoorwaardenLogo from "@/assets/logos/press-arbeidsvoorwaardennieuws.webp.asset.json";

const logos: Record<string, string> = {
  quote: quoteLogo, rabobank: rabobankLogo, deloitte: deloitteLogo,
  // Absolute asset URLs also work when this site is hosted outside Lovable.
  werf: `https://id-preview--c6b7d37e-c3e6-4f87-a779-7288709ae2ba.lovable.app${werfLogo.url}`,
  ondernemer: `https://id-preview--c6b7d37e-c3e6-4f87-a779-7288709ae2ba.lovable.app${ondernemerLogo.url}`,
  arbeidsvoorwaarden: `https://id-preview--c6b7d37e-c3e6-4f87-a779-7288709ae2ba.lovable.app${arbeidsvoorwaardenLogo.url}`,
};

const P = "pressMentions";

type L = { nl: string; en: string; de: string };

const items: { id: string; source: string; href: string; title: L }[] = [
  {
    id: "werf",
    source: "Werf&",
    href: "https://werf-en.nl/blik-op-de-maand-september-2026/",
    title: {
      nl: "De maand in werving: 7 dingen die ons opvielen in september 2026",
      en: "The month in recruitment: 7 things that stood out in September 2026",
      de: "Der Monat im Recruiting: 7 Dinge, die uns im September 2026 aufgefallen sind",
    },
  },
  {
    id: "ondernemer",
    source: "de Ondernemer",
    href: "https://www.deondernemer.nl/personeel/salaris/wet-loontransparantie-werknemers-werkgevers-advies-rick-kuijf-learned~13b0a46",
    title: {
      nl: "wet Loontransparantie in aantocht: wie krijgt het hoogste salaris, je beste medewerker of degene met de grootste mond?",
      en: "Pay transparency legislation is coming: who earns the most, your best employee or the loudest voice?",
      de: "Das Entgelttransparenzgesetz kommt: Wer bekommt das höchste Gehalt, dein bester Mitarbeiter oder der mit der größten Klappe?",
    },
  },
  {
    id: "quote",
    source: "Quote",
    href: "https://www.quotenet.nl/zakelijk/a74142252/hr-systeem-learned-kuijff-3-miljoen-investering/",
    title: {
      nl: "HR-broers van Learned gaan de loonkloof te lijf met €3 miljoen groeigeld",
      en: "Learned's HR brothers take on the pay gap with €3 million in growth capital",
      de: "Die HR-Brüder von Learned gehen mit 3 Mio. € Wachstumskapital gegen die Lohnlücke vor",
    },
  },
  {
    id: "rabobank",
    source: "Rabobank",
    href: "https://www.rabobank.nl/bedrijven/startups-en-scale-ups/artikelen/011525266-learned-eerlijker-beoordelen-en-belonen-met-ai",
    title: {
      nl: "Learned: eerlijker beoordelen en belonen met AI",
      en: "Learned: fairer evaluation and compensation with AI",
      de: "Learned: fairer beurteilen und vergüten mit KI",
    },
  },
  {
    id: "deloitte",
    source: "Deloitte",
    href: "https://www.deloitte.com/nl/en/services/deloitte-private/perspectives/technology-fast-50-and-rising-star-finalists-announced.html",
    title: {
      nl: "Technology Fast 50: genomineerden bekendgemaakt",
      en: "Technology Fast 50 program nominees announced",
      de: "Technology Fast 50: Nominierte bekanntgegeben",
    },
  },
  {
    id: "arbeidsvoorwaarden",
    source: "Arbeidsvoorwaardennieuws.nl",
    href: "https://arbeidsvoorwaardennieuws.nl/news/1049/een-op-de-drie-medewerkers-vindt-dat-leidinggevende-een-cursus-leidinggeven-nodig-heeft",
    title: {
      nl: "Eén op de drie medewerkers vindt dat leidinggevende een cursus leidinggeven nodig heeft",
      en: "One in three employees thinks their manager needs leadership training",
      de: "Jeder dritte Mitarbeiter findet, dass seine Führungskraft einen Führungskurs braucht",
    },
  },
];

const copy = {
  eyebrow: { nl: "In de media", en: "In the media", de: "In den Medien" },
  title: { nl: "Genoemd door", en: "Mentioned by", de: "Erwähnt von" },
  readMore: { nl: "Lees het artikel", en: "Read the article", de: "Artikel lesen" },
  previous: { nl: "Vorige artikelen", en: "Previous articles", de: "Vorherige Artikel" },
  next: { nl: "Volgende artikelen", en: "Next articles", de: "Nächste Artikel" },
};

export const PressMentions = () => {
  const { i18n, t } = useTranslation();
  const lang = (["nl", "en", "de"].includes(i18n.language) ? i18n.language : "nl") as keyof L;
  const [carouselRef, carousel] = useEmblaCarousel({ align: "start", startIndex: 2, slidesToScroll: 1, loop: true });
  const [canPrevious, setCanPrevious] = useState(true);
  const [canNext, setCanNext] = useState(true);
  const updateNavigation = useCallback(() => {
    if (!carousel) return;
    setCanPrevious(carousel.canScrollPrev());
    setCanNext(carousel.canScrollNext());
  }, [carousel]);

  useEffect(() => {
    if (!carousel) return;
    updateNavigation();
    carousel.on("select", updateNavigation).on("reInit", updateNavigation);
    return () => {
      carousel.off("select", updateNavigation).off("reInit", updateNavigation);
    };
  }, [carousel, updateNavigation]);

  return (
    <section className="py-20 px-6">
      <PageContainer>
        <div className="text-center mb-12">
          <T page={P} k="eyebrow" d={copy.eyebrow[lang]} as="p" className="text-xs font-semibold uppercase tracking-wider text-primary mb-2" />
          <h2 className="feature-section-heading text-foreground">
            <T page={P} k="title" d={copy.title[lang]} />
          </h2>
        </div>
        <div ref={carouselRef} className="overflow-hidden">
          <div className="flex -ml-6 touch-pan-y">
          {items.map((item) => (
            <div key={item.id} className="min-w-0 shrink-0 grow-0 basis-full pl-6 md:basis-1/3 flex">
            <a
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex w-full flex-col rounded-3xl border border-border bg-card p-8 shadow-sm transition-shadow hover:shadow-md"
            >
              <div className="h-10 mb-6 flex items-center">
                <EditableImage
                  page={P}
                  section="logos"
                  imageKey={`${item.id}-logo`}
                  src={logos[item.id]}
                  alt={item.source}
                  loading="lazy"
                  className="h-8 w-auto max-w-[140px] object-contain"
                />
              </div>
              <T page={P} k={`items.${item.id}.title`} d={item.title[lang]} as="h3" className="text-lg font-semibold text-foreground mb-6 flex-1" />
              <span className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                <T page={P} k="readMore" d={copy.readMore[lang]} />
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </a>
            </div>
          ))}
          </div>
        </div>
        <div className="mt-6 flex justify-center gap-3">
          <Button variant="outline" size="icon" disabled={!canPrevious} onClick={() => carousel?.scrollPrev()} aria-label={t(`${P}.previous`, { defaultValue: copy.previous[lang] })}>
            <ArrowLeft aria-hidden="true" />
          </Button>
          <Button variant="outline" size="icon" disabled={!canNext} onClick={() => carousel?.scrollNext()} aria-label={t(`${P}.next`, { defaultValue: copy.next[lang] })}>
            <ArrowRight aria-hidden="true" />
          </Button>
        </div>
      </PageContainer>
    </section>
  );
};
